<template>
  <div v-if="data.hits.length">
    <ol :class="listClass">
      <li v-for="hit in data.hits" :key="hit.id" class="w-full min-w-0">
        <slot :hit="hit" />
      </li>
    </ol>

    <NuxtLink
      v-if="showMoreUrl && showMore?.label"
      :to="showMoreUrl"
      class="button is-strong is-link"
    >
      {{ showMore.label }}
    </NuxtLink>
  </div>
  <Message
    v-else-if="hasError"
    error
    :text="
      $texts(
        'contentNotAvailableError',
        'Der Inhalt konnte nicht geladen werden. Bitte versuchen Sie es später noch einmal.',
      )
    "
  />
</template>

<script lang="ts">
import { notNullish } from '@vueuse/core'
import type { SearchFulltextResponse } from '~/layers/site-search/helpers/types'
import type { SearchHandlerTypes } from '~/layers/site-search/types'
</script>

<script setup lang="ts" generic="T extends keyof SearchHandlerTypes">
/**
 * A component to render hits from an Elasticsearch endpoint, without any
 * user-facing filter controls or inputs.
 */
type SearchHit = { id: string } & SearchHandlerTypes[T]

type ReferenceFacet = {
  list?: {
    id?: string | number | undefined | null
  }[]
}

const props = withDefaults(
  defineProps<{
    /**
     * The endpoint to use for querying the index.
     */
    endpoint: T

    /**
     * The number of documents to load per page.
     */
    size?: number

    /**
     * Selected facets from entity reference fields coming from a paragraph.
     *
     * This should only include actual facets. When a "show more" link is
     * rendered, it will use these preselected facet values for the link.
     */
    referenceFacets?: Record<string, ReferenceFacet | undefined | null>

    /**
     * Additional filters (that are not facets) for the query.
     *
     * Use this to add filters to the search query that are not available
     * as facets.
     */
    queryFilters?: Record<string, ReferenceFacet | undefined | null>

    /**
     * Settings for the "Show more" link.
     */
    showMore?: {
      route: string
      label: string
    }

    /**
     * The text to show when no hits were found.
     */
    noResultsText?: string

    /**
     * The CSS class applied to the list tag.
     */
    listClass?:
      | string
      | string[]
      | Record<string, boolean>
      | Record<string, boolean>[]
  }>(),
  {
    listClass: 'grid gap-20',
    size: 6,
    referenceFacets: undefined,
    queryFilters: undefined,
    showMore: undefined,
    noResultsText: undefined,
  },
)

const language = useCurrentLanguage()

function buildReferenceFilter(
  filters?: Record<string, ReferenceFacet | undefined | null>,
): Record<string, string> {
  if (!filters) {
    return {}
  }

  return Object.entries(filters).reduce<Record<string, string>>(
    (acc, [facetKey, field]) => {
      if (field?.list?.length) {
        const ids = field.list
          .map((v) => v.id)
          .filter(notNullish)
          .join(':')
        acc[facetKey] = ids
      }
      return acc
    },
    {},
  )
}

const facets = computed<Record<string, string>>(() =>
  buildReferenceFilter(props.referenceFacets),
)

const filters = computed<Record<string, string>>(() =>
  buildReferenceFilter(props.queryFilters),
)

const query = computed(() => {
  return {
    langcode: language.value,
    size: props.size,
    ...facets.value,
    ...filters.value,
  }
})

const interceptor = useFetchInterceptor()

const { data, status } = await useFetch('/api/search/' + props.endpoint, {
  query,
  deep: false,
  transform: function (v: SearchFulltextResponse<SearchHit>) {
    return { hits: v.hits, error: v.error }
  },
  default: function () {
    return { hits: [], error: false }
  },
  getCachedData: function (key, nuxt) {
    return nuxt.payload.data[key] || nuxt.static.data[key]
  },
  ...interceptor,
})

const hasError = computed<boolean>(
  () => status.value === 'error' || !!data.value.error,
)

const showMoreUrl = computed(() => {
  if (props.showMore?.route) {
    return {
      name: props.showMore.route,
      query: facets.value,
    }
  }

  return null
})

useCDNHeaders((v) => {
  v.addTags(['nuxt:component:search-teaser-list'])
  if (hasError.value) {
    v.setNumeric('maxAge', 5 * 60).setNumeric('staleWhileRevalidate', 30)
  }
})

defineOptions({
  name: 'SearchTeaserList',
})
</script>
